/**
 * The keys Fleece authenticates with, one kind per provider. Each kind has the fields it
 * needs and no others, so an Alpaca key cannot be missing its secret and a Polygon key
 * cannot claim to be live.
 *
 * Every kind comes in three shapes:
 *
 * - `…CredentialInput`: what a caller writes. A secret left out of an update keeps the
 *   stored one, so a rename never has to re-send a key.
 * - `…CredentialSummary`: what is shown. Each secret is reduced to its last four
 *   characters, enough to tell two keys apart and far too little to use one.
 * - `…Credential`: the summary with the secrets. Only what authenticates with them, and
 *   the one route that hands them to such a client, ever holds one.
 */

export type CredentialKind = 'alpaca' | 'polygon';
export const CREDENTIAL_KINDS: ReadonlyArray<CredentialKind> = ['alpaca', 'polygon'];

export const CREDENTIAL_LIMITS = {
  nameLength: 100,
} as const;

/** What every credential carries, whatever its kind. */
export interface CredentialMetadata {
  /** Assigned by the service, and what everything that uses the credential refers to it by. */
  readonly credentialId: string;
  /** Unique, and free to change: "Paper", "Live IRA", "Polygon". */
  readonly name: string;
  /** Bumped on every save; an update must name the one it was made from. A check is not a save. */
  readonly version: number;
  readonly createdAt: number;
  readonly updatedAt: number;
  /**
   * When the provider last answered about these keys: when they were saved, or tested since.
   * Absent for a credential saved before checks were recorded and not tested since.
   */
  readonly checkedAt?: number;
  /**
   * Present when the provider refused the keys at the last check that reached it. Cleared
   * when it accepts them again, or when new keys are saved.
   */
  readonly invalid?: CredentialRefusal;
}

/** Why the provider refuses a credential's keys, and since when. */
export interface CredentialRefusal {
  /** The first check that found them refused, of the run of refusals since. */
  readonly since: number;
  /** In words a person can act on. */
  readonly reason: string;
}

/**
 * What the provider said when asked about a set of keys: Alpaca's `GET /v2/account`, or
 * one ticker's reference data from Polygon. A refusal is an answer rather than an error;
 * an error is a provider that did not answer, which says nothing about the keys.
 */
export type CredentialCheck = AlpacaKeysAccepted | PolygonKeyAccepted | KeysRefused;

export interface AlpacaKeysAccepted {
  readonly kind: 'alpaca';
  readonly valid: true;
  /** Whose keys these are, as Alpaca says. */
  readonly accountId: string;
  /** Alpaca's status for the account, such as ACTIVE. */
  readonly accountStatus: string;
  readonly checkedAt: number;
}

export interface PolygonKeyAccepted {
  readonly kind: 'polygon';
  readonly valid: true;
  readonly checkedAt: number;
}

export interface KeysRefused {
  readonly kind: CredentialKind;
  readonly valid: false;
  readonly reason: string;
  readonly checkedAt: number;
}

export interface AlpacaCredentialSummary extends CredentialMetadata {
  readonly kind: 'alpaca';
  /**
   * Alpaca's account number, as Alpaca reported it when the keys were checked — never as a
   * caller typed it. The same id `BrokerOrder.brokerAccountId` carries.
   */
  readonly accountId: string;
  /** Real money. Which host the keys were checked against, and fixed once they were. */
  readonly live: boolean;
  /** These keys may serve market data. Any Alpaca key can; this says whether to use this one. */
  readonly supportData: boolean;
  /** The service follows this account's order events and books its fills. */
  readonly trackOrders: boolean;
  /**
   * The virtual account an order in this Alpaca account is booked to when nothing claims
   * it — one placed by hand on Alpaca's website, most often. Always one of the same type,
   * live for a live credential and paper for a paper one, so real and simulated money
   * never share an account.
   */
  readonly defaultAccountId: string;
  readonly accessKeyHint: string;
}

export interface AlpacaCredential extends AlpacaCredentialSummary {
  readonly accessKey: string;
  readonly secretKey: string;
}

export interface PolygonCredentialSummary extends CredentialMetadata {
  readonly kind: 'polygon';
  readonly apiKeyHint: string;
}

export interface PolygonCredential extends PolygonCredentialSummary {
  readonly apiKey: string;
}

export type CredentialSummary = AlpacaCredentialSummary | PolygonCredentialSummary;
export type Credential = AlpacaCredential | PolygonCredential;

/**
 * An Alpaca key pair as written. There is no account number: the keys are checked with
 * Alpaca, and Alpaca says whose they are.
 */
export interface AlpacaCredentialInput {
  readonly kind: 'alpaca';
  readonly live: boolean;
  /** Required for a new credential, and replaced together with `secretKey` or not at all. */
  readonly accessKey?: string;
  readonly secretKey?: string;
  /** Defaults to true for a new credential. */
  readonly supportData?: boolean;
  /** Defaults to true for a new credential. */
  readonly trackOrders?: boolean;
  /**
   * Required for a new credential, and a virtual account of the same type: `live` for a
   * live credential, `paper` for a paper one. Left out of an update, the stored one is kept.
   */
  readonly defaultAccountId?: string;
}

export interface PolygonCredentialInput {
  readonly kind: 'polygon';
  /** Required for a new credential; left out of an update, the stored one is kept. */
  readonly apiKey?: string;
}

export type CredentialInput = AlpacaCredentialInput | PolygonCredentialInput;

/**
 * Where an Alpaca account's order feed stands, as the service running it last saw it.
 *
 * - `off`: the credential does not track orders, or the process answering runs no feeds.
 * - `connecting`: opening the stream and waiting for Alpaca to authorize it.
 * - `connected`: authorized and listening.
 * - `reconnecting`: authorized once, and has lost the socket since; it is retrying.
 * - `failed`: Alpaca refused the keys or never answered. Nothing retries a refused key, so
 *   this stays until the credential is changed.
 */
export type FeedState = 'off' | 'connecting' | 'connected' | 'reconnecting' | 'failed';
export const FEED_STATES: ReadonlyArray<FeedState> = ['off', 'connecting', 'connected', 'reconnecting', 'failed'];

export interface FeedStatus {
  readonly state: FeedState;
  /** Why it failed, in words a person can act on. Present only when `failed`. */
  readonly error?: string;
}

/**
 * The last four characters of a secret, for recognising it on a screen. Four is the
 * convention card numbers set, and far too few to search a key space with.
 */
export function secretHint(secret: string): string {
  return secret.length <= 4 ? '…' : `…${secret.slice(-4)}`;
}

/**
 * Strips the secrets, so a credential can be shown. Field by field rather than by
 * omitting the secrets, so a secret added to a kind later is left out until it is
 * deliberately shown, not shown until it is deliberately left out.
 */
export function summarizeCredential(credential: Credential): CredentialSummary {
  const metadata: CredentialMetadata = {
    credentialId: credential.credentialId,
    name: credential.name,
    version: credential.version,
    createdAt: credential.createdAt,
    updatedAt: credential.updatedAt,
    ...(credential.checkedAt === undefined ? {} : { checkedAt: credential.checkedAt }),
    ...(credential.invalid === undefined ? {} : { invalid: credential.invalid }),
  };
  switch (credential.kind) {
    case 'alpaca':
      return {
        ...metadata,
        kind: credential.kind,
        accountId: credential.accountId,
        live: credential.live,
        supportData: credential.supportData,
        trackOrders: credential.trackOrders,
        defaultAccountId: credential.defaultAccountId,
        accessKeyHint: credential.accessKeyHint,
      };
    case 'polygon':
      return { ...metadata, kind: credential.kind, apiKeyHint: credential.apiKeyHint };
  }
}
